"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, CircleQuestionMark as HelpCircle, ShieldCheck, Clock, Search, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const faqItems = [{
  id: "item-1",
  question: "How do you locate hidden leaks without tearing open my walls or floor?",
  answer: "We use non-invasive electronic listening gear, thermal imaging, and pressure testing equipment. This allows our plumbers in Jonesboro to pinpoint underground pipes and in-wall water leaks precisely before making any targeted repairs.",
  category: "Detection Tech"
}, {
  id: "item-2",
  question: "What are the early warning signs of a slab leak in a home?",
  answer: "Common signs include unexplained spikes on your water meter, warm spots on flooring, the sound of running water when fixtures are off, damp baseboards, or sudden drops in overall home water pressure.",
  category: "Warning Signs"
}, {
  id: "item-3",
  question: "How quickly can your emergency plumbing crew arrive in Jonesboro?",
  answer: "We operate 24 hours a day, 7 days a week. For urgent pipe bursts and active water leaks across Jonesboro and Clayton County, our technicians typically arrive within 45 to 60 minutes.",
  category: "Emergency Response"
}, {
  id: "item-4",
  question: "Will my homeowners insurance cover leak detection and repair costs?",
  answer: "Most homeowners policies cover sudden and accidental water damage as well as the cost of tearing out and replacing walls or slabs to access the broken pipe. We provide detailed diagnostic reports and itemized invoices for your insurance adjuster.",
  category: "Insurance & Cost"
}, {
  id: "item-5",
  question: "Can small pinhole pipe leaks be repaired on the spot?",
  answer: "Yes. Our service trucks carry pipe clamps, PEX and copper fittings, and soldering equipment to complete spot repairs immediately after locating the source of the leak.",
  category: "Repairs"
}];
export default function LeakFaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="leakdetectionfaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Header & Quick Assistance Sidebar */}
          <motion.div className="lg:col-span-5 space-y-6" initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }}>
            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                <HelpCircle className="size-3.5 text-accent" />
                Frequently Asked Questions
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              Got questions about leak detection?
            </h2>

            <p className="text-muted-foreground text-base leading-relaxed">
              Hidden water leaks can cause structural damage and high water bills. Find clear answers below on how we find, diagnose, and fix pipe leaks across Jonesboro, GA.
            </p>

            <div className="bg-card border border-border rounded-xl p-6 shadow-sm space-y-4">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 text-primary p-2.5 rounded-lg">
                  <Clock className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-card-foreground text-sm">24/7 Rapid Response</h3>
                  <p className="text-xs text-muted-foreground">Emergency dispatch ready anytime</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="bg-primary/10 text-primary p-2.5 rounded-lg">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-card-foreground text-sm">Non-Destructive Methods</h3>
                  <p className="text-xs text-muted-foreground">Pinpoint accuracy before any cutting</p>
                </div>
              </div>

              <div className="pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground mb-3">
                  Need immediate help locating an active leak?
                </p>
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <Button asChild className="w-full justify-center bg-primary text-primary-foreground hover:bg-primary/90">
                    <a href="tel:4708479076">
                      <Phone className="size-4" />
                      Call Plumber Now
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="w-full justify-center">
                    <Link to="/contact/">Request Service</Link>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Accordion FAQ List */}
          <motion.div className="lg:col-span-7" initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }}>
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-3">
              {faqItems.map((faq, index) => <div key={faq.id} data-index={index}>
                  <AccordionItem value={faq.id} className="bg-card border border-border rounded-xl px-5 py-1 transition-shadow hover:shadow-xs">
                    <AccordionTrigger className="text-left font-semibold text-card-foreground text-base hover:no-underline py-4">
                      <div className="flex items-start gap-3 text-left pr-2">
                        <span className="text-xs font-mono text-muted-foreground mt-1 shrink-0">
                          0{index + 1}
                        </span>
                        <span>{faq.question}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm leading-relaxed pt-1 pb-4 pl-7">
                      <p>{faq.answer}</p>
                      <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-accent font-medium">
                        <Wrench className="size-3" />
                        <span>Topic: {faq.category}</span>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>;
}